import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { box: string; icon: string }> = {
  info: { box: 'border-info/30 bg-info-soft', icon: 'text-info' },
  success: { box: 'border-success/30 bg-success-soft', icon: 'text-success' },
  warning: { box: 'border-warning/30 bg-warning-soft', icon: 'text-warning' },
  danger: { box: 'border-danger/30 bg-danger-soft', icon: 'text-danger' },
};

const ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
} as const;

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Button or link aligned to the right. */
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

/** Inline, non-dismissable notice: form errors, degraded modes, warnings. */
export function Alert({ tone = 'info', title, children, action, icon, className }: AlertProps) {
  const Icon = ICONS[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-lg border px-4 py-3', TONES[tone].box, className)}
    >
      <span className={cn('mt-0.5 shrink-0 [&_svg]:size-4', TONES[tone].icon)} aria-hidden>
        {icon ?? <Icon />}
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        {title && <p className="text-sm font-medium text-fg">{title}</p>}
        {children && <div className="text-xs leading-relaxed text-muted">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}
